import type { BrandSlots } from "@cfg/brand-sdk";
import { useCopy, useLocale } from "@cfg/configurator-ui";

/** Unstyled-but-decent fallbacks for slots a brand does not provide. */
export const DefaultHeader: BrandSlots["Header"] = ({ brand, nav, user, onSignIn, onSignOut, locale }) => {
  const copy = useCopy();
  return (
    <header className="df-header">
      <a href="/" className="df-brand">
        {brand.name}
      </a>
      <nav className="df-nav">
        {nav.map((n) => (
          <a key={n.href} href={n.href} className={n.active ? "is-active" : undefined}>
            {n.label}
          </a>
        ))}
        {locale && locale.options.length > 1 && (
          <select className="cfg-input df-lang" aria-label={copy("locale.label")} value={locale.current} onChange={(e) => locale.onChange(e.target.value)}>
            {locale.options.map((o) => (
              <option key={o.code} value={o.code} lang={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        {user ? (
          <button type="button" className="cfg-btn" onClick={onSignOut}>
            {copy("auth.signOut")}
          </button>
        ) : (
          <button type="button" className="cfg-btn" onClick={onSignIn}>
            {copy("auth.signIn")}
          </button>
        )}
      </nav>
    </header>
  );
};

export const DefaultFooter: BrandSlots["Footer"] = ({ brand, madeWith, links, attribution }) => (
  <footer className="df-footer">
    {brand.tagline || brand.name}
    {madeWith && <small className="df-footer-made">{madeWith}</small>}
    {links && links.length > 0 && (
      <nav className="df-footer-links" aria-label={brand.name}>
        {links.map((l) => (
          <a key={l.href} href={l.href}>
            {l.label}
          </a>
        ))}
      </nav>
    )}
    {attribution && <small className="df-footer-attribution">{attribution}</small>}
    {brand.fitTags.disclaimer && <small className="df-footer-legal">{brand.fitTags.disclaimer}</small>}
  </footer>
);

export const DefaultCatalogCard: BrandSlots["CatalogCard"] = ({ entry, children }) => {
  const copy = useCopy();
  const { locale } = useLocale();
  return (
  <a className={`df-card${entry.kind === "community" ? " df-card-community" : ""}`} href={entry.href}>
    <div className="df-card-preview">{children ?? (entry.thumbnailUrl ? <img src={entry.thumbnailUrl} alt="" loading="lazy" /> : null)}</div>
    <h3>{entry.title}</h3>
    {entry.kind === "community" ? (
      <>
        {entry.fits && entry.fits.length > 0 && (
          <ul className="df-card-fits" aria-label={entry.fitLabel}>
            {entry.fits.slice(0, 3).map((f) => (
              <li key={f.ref} className="df-card-fit" title={f.name}>
                {f.ref}
                {f.name && <span className="df-card-fit-name"> {f.name}</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="df-card-meta">{[entry.designTitle, entry.taggers?.length ? copy("community.taggers", { names: entry.taggers.join(", ") }) : null].filter(Boolean).join(" · ")}</p>
      </>
    ) : (
      entry.description && <p>{entry.description}</p>
    )}
    {entry.variants && (
      <ul className="df-card-variants" aria-label={copy("family.label")}>
        {entry.variants.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>
    )}
    {!!entry.downloads && (
      <p className="df-card-downloads">↓ {entry.downloads === 1 ? copy("downloads.one") : copy("downloads.other", { count: entry.downloads.toLocaleString(locale) })}</p>
    )}
  </a>
  );
};

export const DefaultEmptyState: BrandSlots["EmptyState"] = ({ title, body, action }) => (
  <div className="df-empty">
    <h2>{title}</h2>
    {body && <p>{body}</p>}
    {action}
  </div>
);

export const DefaultConfiguratorFrame: BrandSlots["ConfiguratorFrame"] = ({ children, aside }) => (
  <div className="df-frame">
    {children}
    {aside}
  </div>
);
